<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { DayPilot, DayPilotCalendar } from "@daypilot/daypilot-lite-vue";
import Layout from "../../../Layouts/Authenticated.vue";
import Button from "primevue/button";
import Calendar from "primevue/calendar";
import Tag from "primevue/tag";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import MultiSelect from "primevue/multiselect";
import AutoComplete from "primevue/autocomplete";
import axios from "axios";
import { useToast } from "primevue/usetoast";
import CreateBookingModal from "../../../Components/Calendar/CreateBookingModal.vue";
import BookingDetailModal from "../../../Components/Calendar/BookingDetailModal.vue";
import EditBookingModal from "../../../Components/Calendar/EditBookingModal.vue";
import NoteDetailModal from "../../../Components/Calendar/NoteDetailModal.vue";
import EditNoteModal from "../../../Components/Calendar/EditNoteModal.vue";
import EditProviderBlockModal from "../../../Components/Calendar/EditProviderBlockModal.vue";
import EditProviderBlockSeriesModal from "../../../Components/Calendar/EditProviderBlockSeriesModal.vue";
import { useConfirm } from "primevue/useconfirm";

const toast = useToast();
const confirm = useConfirm();

const toDateString = (date) => {
    if (!date) return "";
    if (typeof date === "string") return date.split("T")[0];
    if (!(date instanceof Date)) return "";

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const toLocalDatetime = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const extractError = (e, defaultMsg = 'Operation failed') => {
    const data = e.response?.data;
    if (data?.errors) {
        const first = Object.values(data.errors).flat()[0];
        if (first) return first;
    }
    return data?.message || e.message || defaultMsg;
};

const formattedDate = computed(() => {
    if (!selectedDate.value) return "";
    const d = selectedDate.value;
    const weekday = new Intl.DateTimeFormat("de-DE", { weekday: "long" }).format(d);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${weekday}, ${day}-${month}-${year}`;
});

const calendarRef              = ref(null);
const calendarWrapperRef       = ref(null);
const calendarScrollContainerRef = ref(null);

/**
 * Dynamically computes remaining viewport height down to BottomNav.
 * Accounts for real-time header, sticky toolbar, and card margins on any device.
 */
const updateCalendarHeight = () => {
    const el = calendarScrollContainerRef.value;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const available = window.innerHeight - rect.top - 78;

    el.style.height = `${Math.max(300, Math.floor(available))}px`;
};

// ── Current Time Line ──────────────────────────────────────────────────────
const currentTimeLineTop = ref(0);
const currentTimeLabel   = ref('');
const showTimeLine       = ref(false);

const isToday = computed(() => {
    const now = new Date(), sel = selectedDate.value;
    return sel.getFullYear() === now.getFullYear() &&
           sel.getMonth()    === now.getMonth()    &&
           sel.getDate()     === now.getDate();
});

// Cached after first render — no DOM reads in the 60s interval
let _firstHourTop = 0;   // px from wrapper top where businessBeginsHour starts
let _pxPerHour    = 0;   // pixels per hour, measured from consecutive row headers

/** Pure math — safe to call in setInterval */
const updateCurrentTimeLine = () => {
    if (_pxPerHour === 0) return;
    const now    = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const begMin = calendarConfig.businessBeginsHour * 60;
    const endMin = calendarConfig.businessEndsHour   * 60;

    if (nowMin < begMin || nowMin > endMin) {
        showTimeLine.value = false;
        return;
    }
    const minutesSinceBegin = nowMin - begMin;
    currentTimeLineTop.value = Math.round(_firstHourTop + minutesSinceBegin * (_pxPerHour / 60));
    currentTimeLabel.value   = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    showTimeLine.value = true;
};

/** Measure from DayPilot row-header elements (hour labels on left: 9:00, 10:00...) */
const initTimeLine = () => {
    setTimeout(() => {
        const wrapper = calendarWrapperRef.value;
        if (!wrapper) return;

        const wrapperTop = wrapper.getBoundingClientRect().top;

        // Row headers = hour labels on the left side. One per hour.
        const rh = wrapper.querySelectorAll('.calendar_default_rowheader');
        if (rh.length >= 2) {
            // Exact Y of first hour (= real header height) and px-per-hour from DOM
            _firstHourTop = rh[0].getBoundingClientRect().top - wrapperTop;
            _pxPerHour    = rh[1].getBoundingClientRect().top - (wrapperTop + _firstHourTop);
        } else {
            // Fallback: use config values (cellHeight=24, cellDuration=15 → 96px/hr)
            // Try colheader for header height, else use 0
            const ch = wrapper.querySelector('.calendar_default_colheader');
            _firstHourTop = ch ? ch.getBoundingClientRect().height : 0;
            _pxPerHour    = (calendarConfig.cellHeight * 60) / calendarConfig.cellDuration;
        }

        const wrapperLeft = wrapper.getBoundingClientRect().left;
        const headers = wrapper.querySelectorAll('.calendar_default_colheader');
        _columnRects = headers.length === calendarConfig.columns.length
            ? calendarConfig.columns.map((col, i) => {
                const r = headers[i].getBoundingClientRect();
                return { id: col.id, left: r.left - wrapperLeft, width: r.width };
            })
            : [];

        updateCurrentTimeLine();
        buildShiftHandles();
    }, 200);
};

// ── Working-hours drag handles ─────────────────────────────────────────────
// Two small grips per provider (shift start/end) layered over the grid. Dragging only
// moves the grip; DayPilot is not re-rendered until the change is saved.
const SHIFT_SNAP_MIN = 15;
let _columnRects = [];
const canEditShifts = ref(false);
const companyWindow = ref({ startMin: 0, endMin: 24 * 60 });
const shiftHandles  = ref([]);
const shiftDrag     = ref(null);

const minutesToHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const minutesToTop  = (m) => _firstHourTop + (m - calendarConfig.businessBeginsHour * 60) * (_pxPerHour / 60);

const buildShiftHandles = () => {
    if (!canEditShifts.value || isClosed.value || _pxPerHour === 0) {
        shiftHandles.value = [];
        return;
    }
    const handles = [];
    _columnRects.forEach(({ id, left, width }) => {
        const shift = availabilityLookup.value[id]?.provider[0];
        if (!shift) return;
        ['start', 'end'].forEach((edge) => {
            handles.push({
                key: `${id}-${edge}`,
                providerId: id,
                edge,
                minutes: edge === 'start' ? shift.startMin : shift.endMin,
                startMin: shift.startMin,
                endMin: shift.endMin,
                left,
                width,
            });
        });
    });
    shiftHandles.value = handles;
};

const handleMinutes = (h) => {
    const d = shiftDrag.value;
    if (d?.providerId !== h.providerId) return h.minutes;
    return h.edge === 'start' ? d.newStart : d.newEnd;
};

// Dragging the start moves the whole shift, so both of that provider's grips light up.
const isHandleActive = (h) => {
    const d = shiftDrag.value;
    return d?.providerId === h.providerId && (d.edge === 'start' || d.key === h.key);
};

const startShiftDrag = (e, h) => {
    if (e.button !== 0 || shiftDrag.value) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    shiftDrag.value = { ...h, originY: e.clientY, dragging: true, newStart: h.startMin, newEnd: h.endMin };
};

const moveShiftDrag = (e) => {
    const d = shiftDrag.value;
    if (!d?.dragging) return;
    const deltaMin = (e.clientY - d.originY) / (_pxPerHour / 60);
    const snap = (m) => Math.round(m / SHIFT_SNAP_MIN) * SHIFT_SNAP_MIN;
    const { startMin: companyStart, endMin: companyEnd } = companyWindow.value;

    let newStart = d.startMin;
    let newEnd = d.endMin;
    if (d.edge === 'start') {
        // Start grip = later/earlier shift: keeps the shift length, stays inside company hours.
        const shift = snap(d.startMin + deltaMin) - d.startMin;
        const clamped = Math.min(companyEnd - d.endMin, Math.max(companyStart - d.startMin, shift));
        newStart += clamped;
        newEnd += clamped;
    } else {
        // End grip = overtime / leaving early.
        newEnd = Math.min(companyEnd, Math.max(d.startMin + SHIFT_SNAP_MIN, snap(d.endMin + deltaMin)));
    }

    if (newStart !== d.newStart || newEnd !== d.newEnd) {
        d.newStart = newStart;
        d.newEnd = newEnd;
    }
};

// Booking datetimes are naive clinic-local "Y-m-d H:i:s"; read the wall clock directly.
const bookingMinutes = (dt) => {
    const m = /[ T](\d{2}):(\d{2})/.exec(dt || '');
    return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
};

const endShiftDrag = () => {
    const d = shiftDrag.value;
    if (!d?.dragging) return;
    d.dragging = false;

    const startMin = d.newStart;
    const endMin   = d.newEnd;
    if (startMin === d.startMin && endMin === d.endMin) {
        shiftDrag.value = null;
        return;
    }

    const outside = rawBookings.value.filter((b) => {
        if (String(b.provider_id) !== d.providerId) return false;
        if (['cancelled_by_customer', 'cancelled_by_staff'].includes((b.status || '').toLowerCase())) return false;
        return bookingMinutes(b.blocked_start_datetime || b.start_datetime) < startMin
            || bookingMinutes(b.blocked_end_datetime || b.end_datetime) > endMin;
    }).length;

    const name = providerById.value[d.providerId]?.name || '';
    const range = `${minutesToHHMM(startMin)} – ${minutesToHHMM(endMin)}`;
    const warning = outside ? `\nAchtung: ${outside} Buchung(en) liegen ausserhalb der neuen Arbeitszeit.` : '';

    confirm.require({
        message: `Arbeitszeit von ${name} für diesen Tag auf ${range} ändern?\nDie Kunden-Buchungszeit wird automatisch mit angepasst.${warning}`,
        header: 'Arbeitszeit ändern',
        icon: 'pi pi-clock',
        rejectLabel: 'Nein',
        acceptLabel: 'Ja',
        accept: async () => {
            try {
                await axios.post('/ajax/admin/provider-special-days/day-shift', {
                    provider_id: Number(d.providerId),
                    date: toDateString(selectedDate.value),
                    start_time: minutesToHHMM(startMin),
                    end_time: minutesToHHMM(endMin),
                });
                toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Arbeitszeit geändert.', life: 3000 });
                await loadDay();
            } catch (error) {
                toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(error, 'Arbeitszeit konnte nicht geändert werden.'), life: 5000 });
            } finally {
                shiftDrag.value = null;
            }
        },
        reject: () => { shiftDrag.value = null; },
        onHide: () => { shiftDrag.value = null; },
    });
};

let _resizeRemeasureTimer = null;
const remeasureOnResize = () => {
    clearTimeout(_resizeRemeasureTimer);
    _resizeRemeasureTimer = setTimeout(initTimeLine, 150);
};
// ───────────────────────────────────────────────────────────────────────────

let timeLineInterval   = null;
let pollingInterval    = null;   // background sync timer
const POLL_INTERVAL_MS = 30_000; // poll every 30 seconds
const STORAGE_KEY      = 'calendar_quick_filter'; // localStorage key for preset persistence
// ───────────────────────────────────────────────────────────────────────────

const toggleCalendar = () => {
    // PrimeVue Calendar opens on focus/click of its internal input
    const input = calendarRef.value?.$el?.querySelector("input");
    if (input) {
        input.click();
    }
};

const toDateTime = (date, time) => {
    if (!date || !time) {
        return null;
    }

    return `${date}T${time}:00`;
};

const selectedDate = ref(new Date());
const loading = ref(false);

// Raw server day data
const rawProviders = ref([]);
const rawBookings = ref([]);
const rawNotes = ref([]);
const rawProviderBlocks = ref([]);
const rawAvailability = ref([]);
const rawProviderSpecialDayReasons = ref({});

// Filtered data displayed on calendar
const providers = ref([]);
const bookings = ref([]);
const notes = ref([]);
const providerBlocks = ref([]);
const availability = ref([]);

const noteTypes = ref([]);
const selectedSlot = ref(null);
const selectedBooking = ref(null);
const selectedNote = ref(null);
const selectedBlock = ref(null);
const modalVisible = ref(false);
const detailModalVisible = ref(false);
const editModalVisible = ref(false);
const noteDetailVisible = ref(false);
const editNoteVisible = ref(false);
const editBlockVisible = ref(false);
const editBlockSeriesVisible = ref(false);
const serviceOptions = ref([]);
const providerOptions = ref([]);
const isClosed = ref(false);
const updatingStatus = ref(false);

const showFilter = ref(false);
const filters = reactive({
    customer: null,
    booking_code: '',
    booking_filter: 'non_cancelled',
    show_notes: 'both',
    service_ids: [],
    provider_ids: []
});

const bookingFilterOptions = [
    { label: 'Show all bookings', value: 'all' },
    { label: 'Cancelled bookings', value: 'cancelled' },
    { label: 'Non-cancelled bookings', value: 'non_cancelled' }
];

const showNotesOptions = [
    { label: 'Show bookings and notes', value: 'both' },
    { label: 'Show only notes', value: 'notes_only' },
    { label: 'Show only bookings', value: 'bookings_only' }
];

const customerSuggestions = ref([]);
const searchCustomers = async (event) => {
    try {
        const { data } = await axios.get("/ajax/customers", {
            params: { s: event.query },
        });
        customerSuggestions.value = data.customers.data.map(c => ({
            id: c.id,
            name: `${c.first_name} ${c.last_name}`,
            phone: c.phone,
            label: `${c.first_name} ${c.last_name} ${c.phone || ''}`.trim()
        }));
    } catch (error) {
        customerSuggestions.value = [];
    }
};

const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const parts = timeStr.split(':');
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
};

// Fast client-side instant filtering (< 20ms)
const applyFilters = () => {
    // Refetch: booking_filter is resolved server-side, so switching between
    // cancelled / non-cancelled needs fresh data. fetchDayData() rebuilds the
    // scheduler model and the timeline itself once the response lands.
    fetchDayData(true);
};

// ── Quick-filter presets ────────────────────────────────────────────────────
/** Which preset button is currently active: null | 'infrashape' | 'infrashape_sauna' */
const activePreset = ref(null);

/**
 * Apply a named preset by matching provider names.
 * Saves the choice to localStorage so it survives page reloads.
 * Calling the same preset again acts as a toggle (clears it).
 */
const applyPreset = (preset) => {
    if (activePreset.value === preset) {
        // Toggle off
        activePreset.value = null;
        filters.provider_ids = [];
        localStorage.removeItem(STORAGE_KEY);
    } else {
        activePreset.value = preset;
        localStorage.setItem(STORAGE_KEY, preset);
        _applyPresetToFilter(preset);
    }
    buildSchedulerModel(toDateString(selectedDate.value));
    initTimeLine();
};

/** Internal: maps preset name → provider objects from providerOptions */
const _applyPresetToFilter = (preset) => {
    if (!preset || providerOptions.value.length === 0) return;
    if (preset === 'infrashape') {
        filters.provider_ids = providerOptions.value.filter(p =>
            /infrashap/i.test(p.name)
        );
    } else if (preset === 'infrashape_sauna') {
        filters.provider_ids = providerOptions.value.filter(p =>
            /infrashap|sauna/i.test(p.name)
        );
    }
};

/** Called on mount and whenever providerOptions loads — restores persisted preset */
const restorePreset = () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && providerOptions.value.length > 0) {
        activePreset.value = saved;
        _applyPresetToFilter(saved);
        buildSchedulerModel(toDateString(selectedDate.value));
        initTimeLine();
    }
};
// ── End quick-filter presets ────────────────────────────────────────────────

const clearFilters = () => {
    filters.customer = null;
    filters.booking_code = '';
    filters.booking_filter = 'non_cancelled';
    filters.show_notes = 'both';
    filters.service_ids = [];
    filters.provider_ids = [];
    activePreset.value = null;
    localStorage.removeItem(STORAGE_KEY);
    // booking_filter resets to non_cancelled, which is a server-side filter — refetch.
    fetchDayData(true);
};


const providerById = computed(() => {
    return rawProviders.value.reduce((map, provider) => {
        map[String(provider.id)] = provider;
        return map;
    }, {});
});

const escapeHtml = (text) => {
    if (!text) return '';
    return text.toString()
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
};

const availabilityLookup = computed(() => {
    const map = {};
    if (!rawAvailability.value) return map;
    rawAvailability.value.forEach((slot) => {
        const resource = String(slot.provider_id);
        if (!map[resource]) map[resource] = { customer: [], provider: [] };
        const startMin = timeToMinutes(slot.start);
        const endMin = timeToMinutes(slot.end);
        if (slot.type === 'customer_available' || slot.type === 'available') {
            map[resource].customer.push({ startMin, endMin });
        } else if (slot.type === 'provider_available') {
            map[resource].provider.push({ startMin, endMin });
        }
    });
    return map;
});

const calendarConfig = reactive({
    viewType: "Resources",
    timeFormat: "Clock24Hours",
    startDate: toDateString(new Date()),
    showCurrentTime: true,
    columns: [],
    events: [],
    cellDuration: 15,
    businessBeginsHour: 8,
    businessEndsHour: 22,
    allowEventOverlap: true,
    eventStackingLineHeight: 100,
    durationBarVisible: false,
    eventMoveHandling: "Update",
    timeRangeSelectedHandling: "Enabled",
    heightSpec: "BusinessHoursNoScroll",
    cellHeight: 24,
    eventMarginRight: 22,
    onTimeRangeSelected: (args) => {
        const resource = String(args.resource);
        const slots = availabilityLookup.value[resource] || { customer: [], provider: [] };
        
        const cellStartMin = args.start.getHours() * 60 + args.start.getMinutes();
        const cellEndMin = args.end.getHours() * 60 + args.end.getMinutes();

        const isCustomerAvailable = slots.customer.some(s => s.startMin < cellEndMin && s.endMin > cellStartMin);
        const isProviderAvailable = slots.provider.some(s => s.startMin < cellEndMin && s.endMin > cellStartMin);

        if (!isCustomerAvailable && !isProviderAvailable) {
            toast.add({ severity: 'warn', summary: 'Nicht verfügbar', detail: 'Dieser Slot ist nicht buchbar.', life: 3000 });
            selectedSlot.value = null;
            return;
        }
        const provider = providerById.value[String(args.resource)] || null;
        selectedSlot.value = {
            start: args.start.toString("yyyy-MM-dd HH:mm:ss"),
            end: args.end.toString("yyyy-MM-dd HH:mm:ss"),
            resource: String(args.resource),
            providerUuid: provider?.uuid || String(args.resource),
            providerName: provider?.name || `Provider ${args.resource}`,
        };
        modalVisible.value = true;
    },

    onEventClick: async (args) => {
        const eventData = args.e?.data;
        
        if (args.originalEvent?.shiftKey === true) {
            if (eventData?.isProviderBlock || eventData?.isNote) return;
            
            const uuid = args.e.id();
            if (!uuid) return;
            
            if (updatingStatus.value) return;
            updatingStatus.value = true;
            
            const currentStatus = (eventData?.data?.status || 'pending').toLowerCase();
            const statusFlow = {
                'pending': 'confirmed',
                'confirmed': 'ready',
                'ready': 'completed',
                'completed': 'no_show',
                'no_show': 'pending'
            };
            const newStatus = statusFlow[currentStatus] || 'confirmed';

            try {
                await axios.patch(`/ajax/admin/bookings/${uuid}/status`, { status: newStatus });
                const targetBooking = rawBookings.value.find(b => String(b.uuid) === String(uuid));
                if (targetBooking) {
                    targetBooking.status = newStatus;
                }
                if (eventData?.data) {
                    eventData.data.status = newStatus;
                }
                buildSchedulerModel(toDateString(selectedDate.value));
                toast.add({ severity: 'success', summary: 'Erfolg', detail: `Status auf ${newStatus.toUpperCase()} aktualisiert`, life: 3000 });
            } catch (error) {
                toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(error, "Status konnte nicht aktualisiert werden."), life: 5000 });
            } finally {
                updatingStatus.value = false;
            }
            return;
        }

        if (eventData?.isProviderBlock) {
            selectedBlock.value = eventData.data;
            editBlockVisible.value = true;
            return;
        }
        if (eventData?.isNote) {
            selectedNote.value = eventData;
            noteDetailVisible.value = true;
            return;
        }
        
        selectedBooking.value = eventData;
        detailModalVisible.value = true;
    },

    onEventMoved: async (args) => {
        const uuid = args.e.id();
        const isNote = !!args.e.data.isNote;
        const isProviderBlock = !!args.e.data.isProviderBlock;
        
        if (!uuid) {
            toast.add({ severity: 'error', summary: 'Fehler', detail: 'ID nicht gefunden.', life: 3000 });
            await loadDay();
            return;
        }

        const durationMs = args.e.end().getTime() - args.e.start().getTime();
        const newStartStr = args.newStart.toString("yyyy-MM-dd HH:mm:ss");
        const newEndStr = args.newStart.addMilliseconds(durationMs).toString("yyyy-MM-dd HH:mm:ss");
        const providerUuid = providerById.value[String(args.newResource)]?.uuid || String(args.newResource);

        confirm.require({
            message: isProviderBlock ? 'Möchten Sie diese Blockierung wirklich verschieben?' : (isNote ? 'Möchten Sie diese Notiz wirklich verschieben?' : 'Möchten Sie diese Buchung wirklich verschieben?'),
            header: 'Bestätigung',
            icon: 'pi pi-exclamation-triangle',
            rejectLabel: 'Nein',
            acceptLabel: 'Ja',
            accept: async () => {
                try {
                    if (isProviderBlock) {
                        await axios.put(`/management/provider-blocks/${uuid}`, {
                            title: args.e.data.title,
                            notes: args.e.data.notes,
                            date: newStartStr.split(' ')[0],
                            start_datetime: newStartStr,
                            end_datetime: newEndStr,
                            provider_id: providerUuid,
                        });
                        toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Blockierung verschoben.', life: 3000 });
                    } else if (isNote) {
                        await axios.patch(`/ajax/admin/notes/${uuid}`, {
                            start_datetime: newStartStr,
                            end_datetime: newEndStr,
                            provider_id: providerUuid,
                        });
                        toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Notiz verschoben.', life: 3000 });
                    } else {
                        await axios.patch(`/ajax/admin/bookings/${uuid}/reschedule`, {
                            start_datetime: newStartStr,
                            provider_id: providerUuid,
                        });
                        toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Buchung verschoben.', life: 3000 });
                    }
                    await loadDay();
                } catch (error) {
                    toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(error, (isProviderBlock ? "Blockierung" : (isNote ? "Notiz" : "Buchung")) + " konnte nicht verschoben werden."), life: 5000 });
                    await loadDay();
                }
            },
            reject: async () => {
                await loadDay();
            },
            onHide: async () => {
                await loadDay();
            }
        });
    },

    onEventResized: async (args) => {
        const uuid = args.e.id();
        const isNote = !!args.e.data.isNote;
        const isProviderBlock = !!args.e.data.isProviderBlock;
        
        if (!uuid) {
            toast.add({ severity: 'error', summary: 'Fehler', detail: 'ID nicht gefunden.', life: 3000 });
            await loadDay();
            return;
        }

        const newStartStr = args.newStart.toString("yyyy-MM-dd HH:mm:ss");
        const newEndStr = args.newEnd.toString("yyyy-MM-dd HH:mm:ss");
        const providerUuid = providerById.value[String(args.e.resource())]?.uuid || String(args.e.resource());

        confirm.require({
            message: isProviderBlock ? 'Möchten Sie die Zeit für diese Blockierung wirklich ändern?' : (isNote ? 'Möchten Sie die Zeit für diese Notiz wirklich ändern?' : 'Möchten Sie die Zeit für diese Buchung wirklich ändern?'),
            header: 'Bestätigung',
            icon: 'pi pi-exclamation-triangle',
            rejectLabel: 'Nein',
            acceptLabel: 'Ja',
            accept: async () => {
                try {
                    if (isProviderBlock) {
                        await axios.put(`/management/provider-blocks/${uuid}`, {
                            title: args.e.data.title,
                            notes: args.e.data.notes,
                            date: newStartStr.split(' ')[0],
                            start_datetime: newStartStr,
                            end_datetime: newEndStr,
                            provider_id: providerUuid,
                        });
                        toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Blockierung geändert.', life: 3000 });
                    } else if (isNote) {
                        await axios.patch(`/ajax/admin/notes/${uuid}`, {
                            start_datetime: newStartStr,
                            end_datetime: newEndStr,
                            provider_id: providerUuid,
                        });
                        toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Notiz geändert.', life: 3000 });
                    } else {
                        await axios.patch(`/ajax/admin/bookings/${uuid}/reschedule`, {
                            start_datetime: newStartStr,
                            end_datetime: newEndStr,
                            provider_id: providerUuid,
                        });
                        toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Buchung geändert.', life: 3000 });
                    }
                    await loadDay();
                } catch (error) {
                    toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(error, (isProviderBlock ? "Blockierung" : (isNote ? "Notiz" : "Buchung")) + " konnte nicht geändert werden."), life: 5000 });
                    await loadDay();
                }
            },
            reject: async () => {
                await loadDay();
            },
            onHide: async () => {
                await loadDay();
            }
        });
    },

    onBeforeCellRender: (args) => {
        const resource = String(args.cell.resource);
        const slots = availabilityLookup.value[resource] || { customer: [], provider: [] };

        // Instant integer math: No string splitting or Date object creation across 700+ cells
        const cellStartMin = args.cell.start.getHours() * 60 + args.cell.start.getMinutes();
        const cellEndMin = args.cell.end.getHours() * 60 + args.cell.end.getMinutes();

        const isCustomerAvailable = slots.customer.some(s => s.startMin < cellEndMin && s.endMin > cellStartMin);
        const isProviderAvailable = slots.provider.some(s => s.startMin < cellEndMin && s.endMin > cellStartMin);

        args.cell.properties.backColor = "#ffffff";
        args.cell.properties.fontColor = "#1f2937";
        
        let baseClass = "blocked-cell";
        let isBusiness = false;

        if (isCustomerAvailable) {
            baseClass = "available-cell";
            isBusiness = true;
        } else if (isProviderAvailable) {
            baseClass = "provider-only-cell";
            isBusiness = true;
        }

        const isHourEnd = args.cell.end.getMinutes() === 0;
        args.cell.properties.cssClass = isHourEnd ? `${baseClass} hour-line` : baseClass;

        args.cell.properties.text = "";

        // Reason note (e.g. "üK", "Geburtstag") for a provider's special day — closed
        // (blocked-cell) or just modified hours (available/provider-only cell), staff want it
        // visible either way. Stamped once, on the first business-hour cell of the column, so
        // it isn't repeated across ~56 stacked 15-minute cells for the same provider.
        const specialDayReason = rawProviderSpecialDayReasons.value[resource];
        if (specialDayReason && cellStartMin === calendarConfig.businessBeginsHour * 60) {
            const escapedReason = escapeHtml(specialDayReason);
            args.cell.properties.html = `<div class="special-day-note"><span title="${escapedReason}">${escapedReason}</span></div>`;
        }

        args.cell.properties.business = isBusiness;
    },
});

const loadServices = async () => {
    try {
        const { data } = await axios.get("/ajax/admin/services", {
            params: { all: 1 },
        });

        const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        serviceOptions.value = rows.map((service) => ({
            id: service.id,
            uuid: service.uuid,
            name: service.name,
            duration: service.duration || 0,
            buffer_before: service.buffer_before_minutes || 0,
            buffer_after: service.buffer_after_minutes || 0,
        }));
    } catch (error) {
        serviceOptions.value = [];
    }
};

const loadNoteTypes = async () => {
    try {
        const { data } = await axios.get("/ajax/admin/note-types", { params: { all: 1 } });
        const rows = data?.data || data || [];
        noteTypes.value = rows.map(t => ({
            id: t.id,
            uuid: t.uuid,
            name: t.name,
            color: t.color
        }));
    } catch (e) {
        noteTypes.value = [];
    }
};

const loadProviders = async () => {
    try {
        const { data } = await axios.get("/ajax/admin/providers", { params: { all: 1 } });
        const rows = data?.data || data || [];
        providerOptions.value = rows.map(p => ({
            id: p.id,
            uuid: p.uuid,
            name: p.name,
        }));
    } catch (e) {
        providerOptions.value = [];
    }
};

const closeDetail = () => {
    detailModalVisible.value = false;
    selectedBooking.value = null;
};

const openEdit = (booking) => {
    if (booking) {
        selectedBooking.value = booking;
    }
    detailModalVisible.value = false;
    editModalVisible.value = true;
};

const openEditSeriesModal = () => {
    editBlockSeriesVisible.value = true;
};

const handleUpdated = async () => {
    editModalVisible.value = false;
    await loadDay();
};

const handleCancelled = async () => {
    detailModalVisible.value = false;
    await loadDay();
};

const providerCount = computed(() => providers.value.length);
const bookingCount = computed(() => bookings.value.length);

const formatTimeOnly = (isoStr) => {
    if (!isoStr) return "";
    const date = new Date(isoStr);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const buildSchedulerModel = (date) => {
    // 1. Filter Providers locally
    const selectedProviderIds = (filters.provider_ids || []).map(p => p.id);
    let activeProviders = rawProviders.value;
    if (selectedProviderIds.length > 0) {
        activeProviders = activeProviders.filter(p => selectedProviderIds.includes(p.id));
    }
    providers.value = activeProviders;

    calendarConfig.columns = activeProviders.map((provider) => ({
        id: String(provider.id),
        name: provider.name,
    }));

    // 2. Filter Bookings locally
    const activeProviderIdSet = new Set(activeProviders.map(p => p.id));
    const selectedServiceIds = (filters.service_ids || []).map(s => s.id);
    const filterCustomer = filters.customer;
    const bookingCode = (filters.booking_code || '').trim().toLowerCase();
    const showNotes = filters.show_notes || 'both';
    const bookingFilter = filters.booking_filter || 'non_cancelled';

    let activeBookings = [];
    if (showNotes !== 'notes_only') {
        activeBookings = rawBookings.value.filter((booking) => {
            if (!activeProviderIdSet.has(booking.provider_id)) return false;
            
            if (filterCustomer?.id && booking.customer_id !== filterCustomer.id) return false;
            
            if (bookingCode && !(booking.uuid && booking.uuid.toLowerCase().includes(bookingCode))) return false;
            
            if (selectedServiceIds.length > 0 && !selectedServiceIds.includes(booking.service_id)) return false;
            
            const s = (booking.status || '').toLowerCase();
            const isCancelled = ['cancelled_by_customer', 'cancelled_by_staff'].includes(s);
            if (bookingFilter === 'cancelled' && !isCancelled) return false;
            if (bookingFilter === 'non_cancelled' && isCancelled) return false;
            
            return true;
        });
    }
    bookings.value = activeBookings;

    // 3. Filter Notes locally
    let activeNotes = [];
    if (showNotes !== 'bookings_only') {
        activeNotes = rawNotes.value.filter((note) => activeProviderIdSet.has(note.provider_id));
    }
    notes.value = activeNotes;

    // 4. Filter Provider Blocks locally
    let activeBlocks = [];
    if (showNotes !== 'bookings_only') {
        activeBlocks = rawProviderBlocks.value.filter((block) => activeProviderIdSet.has(block.provider_id));
    }
    providerBlocks.value = activeBlocks;

    // 5. Build Event Objects for DayPilot
    const bookingEvents = activeBookings
        .map((booking) => {
            if (!booking.start_datetime || !booking.end_datetime || !booking.provider_id) {
                return null;
            }

            let customerName = [booking.customer?.first_name, booking.customer?.last_name]
                .filter(Boolean)
                .join(" ")
                .trim() || "Customer";

            // Neoprene suit size (e.g. XS, S, M, L, XL, XXL, XXXL)
            const suitSize = booking.customer?.suit_size ?? null;

            const startTime = formatTimeOnly(booking.blocked_start_datetime || booking.start_datetime);
            const endTime = formatTimeOnly(booking.blocked_end_datetime || booking.end_datetime);
            const timeRange = `${startTime} - ${endTime}`;

            const s = (booking.status || '').toLowerCase();
            
            let backColor = "#ea747f"; // confirmed (coral-red)
            let borderColor = "#b83543";
            let fontColor = "#000000";
            
            if (s === 'pending') {
                backColor = "#dc2626"; // red
                borderColor = "#991b1b";
                fontColor = "#ffffff";
            } else if (s === 'confirmed') {
                backColor = "#ea747f"; // vibrant coral-red
                borderColor = "#b83543";
                fontColor = "#000000";
            } else if (s === 'ready') {
                backColor = "#3ebd6d"; // green
                borderColor = "#1e8745";
                fontColor = "#000000";
            } else if (s === 'completed') {
                backColor = "#15803d"; // dark green
                borderColor = "#14532d";
                fontColor = "#ffffff";
            } else if (s === 'no_show') {
                backColor = "#ea580c"; // orange
                borderColor = "#9a3412";
                fontColor = "#ffffff";
            } else if (['cancelled_by_customer', 'cancelled_by_staff'].includes(s)) {
                backColor = "#fca5a5";
                borderColor = "#f87171";
                fontColor = "#1f2937";
            }

            // Extract resource name & number for prominent display
            let rawResource = booking.service?.name || "Service";
            if (booking.reserved_resource?.name) {
                rawResource = booking.reserved_resource.name;
            } else if (booking.resource_reservations?.length > 0 && booking.resource_reservations[0].device_resource?.name) {
                rawResource = booking.resource_reservations[0].device_resource.name;
            } else if (booking.device?.name) {
                rawResource = booking.device.name;
            }

            // Extract number (e.g. #7, #11, 2)
            const numMatch = rawResource.match(/#(\d+)/) || rawResource.match(/\b(\d+)\b/);
            const resourceNumber = numMatch ? numMatch[1] : null;

            let cleanName = resourceNumber
                ? rawResource.replace(/#\d+/, '').replace(new RegExp(`\\b${resourceNumber}\\b`), '').replace(/#/, '').trim()
                : rawResource;
            cleanName = cleanName.replace(/^[.\s\-_/:]+/, '').trim();

            const badgeBg = fontColor === '#ffffff' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.22)';
            const sizeBadgeBg = fontColor === '#ffffff' ? 'rgba(255,255,255,0.30)' : 'rgba(0,0,0,0.20)';

            // Resource number badge - placed first on Line 3: [Nr. 10]
            const resourceNumberBadge = resourceNumber
                ? `<span style="display: inline-block; font-weight: 900; background: ${badgeBg}; color: ${fontColor}; padding: 0 4px; border-radius: 3px; font-size: 10px; border: 1px solid rgba(0,0,0,0.15); flex-shrink: 0; line-height: 14px;">Nr. ${resourceNumber}</span>`
                : '';

            // Suit size badge - placed second on Line 3: [XXL]
            const suitSizeBadgeHtml = suitSize
                ? `<span style="display: inline-block; font-weight: 900; font-size: 10px; background: ${sizeBadgeBg}; color: ${fontColor}; padding: 0 4px; border-radius: 3px; border: 1px solid rgba(0,0,0,0.18); letter-spacing: 0.3px; flex-shrink: 0; line-height: 14px;" title="Größe: ${suitSize}">${suitSize}</span>`
                : '';

            // Line 3: [Nr. 10] [XXL] Infrashape
            let line3Html = "";
            if (suitSizeBadgeHtml || resourceNumberBadge) {
                line3Html = `
                    <div style="display: flex; align-items: center; gap: 3px; margin-top: 1px; min-width: 0; overflow: hidden; flex-shrink: 0; line-height: 14px;" title="${rawResource}${suitSize ? ' (Größe: ' + suitSize + ')' : ''}">
                        ${resourceNumberBadge}
                        ${suitSizeBadgeHtml}
                        <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600; font-size: 11px;">${cleanName || rawResource}</span>
                    </div>
                `;
            } else {
                line3Html = `<div style="margin-top: 1px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600; font-size: 11px; flex-shrink: 0; line-height: 14px;" title="${rawResource}">${rawResource}</div>`;
            }

            const htmlContent = `
                <div style="font-size: 11px; line-height: 1; padding: 2px 4px; overflow: hidden; height: 100%; box-sizing: border-box; display: flex; flex-direction: column; justify-content: flex-start; color: ${fontColor}; font-family: inherit;">
                    <div style="font-size: 10px; font-weight: 700; opacity: 0.95; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex-shrink: 0; line-height: 10px;">${timeRange}</div>
                    <div style="font-size: 12px; font-weight: 800; color: ${fontColor}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; flex-shrink: 0; line-height: 14px;" title="${customerName}">${customerName}</div>
                    ${line3Html}
                </div>
            `;

            return {
                id: String(booking.uuid),
                start: booking.blocked_start_datetime || booking.start_datetime,
                end: booking.blocked_end_datetime || booking.end_datetime,
                resource: String(booking.provider_id),
                html: htmlContent,
                text: `${timeRange} \n${customerName} \n${rawResource}${suitSize ? ' \n' + suitSize : ''}`,
                backColor: backColor,
                borderColor: borderColor,
                fontColor: fontColor,
                data: booking,
            };
        })
        .filter(Boolean);

    const noteEvents = activeNotes
        .map((note) => {
            if (!note.start_datetime || !note.end_datetime || !note.provider_id) {
                return null;
            }

            const startTime = formatTimeOnly(note.start_datetime);
            const endTime = formatTimeOnly(note.end_datetime);
            const timeRange = `${startTime} - ${endTime}`;

            const htmlContent = `
                <div style="font-size: 11px; line-height: 1; padding: 1px 3px; overflow: hidden;">
                    <div style="font-weight: 700;">${timeRange}</div>
                    <div style="margin-top: 1px; font-weight: 500; opacity: 0.9; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden;" title="${note.note || "Notiz"}">${note.note || "Notiz"}</div>
                </div>
            `;

            return {
                id: String(note.uuid),
                start: note.start_datetime,
                end: note.end_datetime,
                resource: String(note.provider_id),
                html: htmlContent,
                text: `${timeRange} \n${note.note || "Notiz"}`,
                backColor: note.type?.color || "#FDE68A",
                borderColor: "transparent",
                fontColor: "#78350F",
                cssClass: note.is_time_blocked ? "" : "note-not-blocking",
                data: note,
                isNote: true, 
            };
        })
        .filter(Boolean);
        
    const blockEvents = activeBlocks
        .map((block) => {
            if (!block.start_datetime || !block.end_datetime || !block.provider_id) {
                return null;
            }

            const startTime = formatTimeOnly(block.start_datetime);
            const endTime = formatTimeOnly(block.end_datetime);
            const timeRange = `${startTime} - ${endTime}`;

            const htmlContent = `
                <div style="font-size: 11px; line-height: 1; padding: 1px 3px; overflow: hidden;">
                    <div style="font-weight: 700;">${timeRange}</div>
                    <div style="margin-top: 1px; font-weight: 500; opacity: 0.9; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden;" title="${block.title || "Block"}">${block.title || "Block"}</div>
                </div>
            `;

            return {
                id: String(block.uuid),
                start: block.start_datetime,
                end: block.end_datetime,
                resource: String(block.provider_id),
                html: htmlContent,
                text: `${timeRange} \n${block.title || "Block"}`,
                backColor: "#34BBF1",
                borderColor: "transparent",
                fontColor: "#ffffff",
                cssClass: "",
                data: block,
                isProviderBlock: true, 
            };
        })
        .filter(Boolean);

    calendarConfig.events = [...bookingEvents, ...noteEvents, ...blockEvents];
    calendarConfig.startDate = date;
};

/**
 * Shared fetch logic. `showSpinner` = true for manual loads (shows the Laden
 * spinner), false for background polling (silent, no UI flicker).
 */
const fetchDayData = async (showSpinner = true) => {
    if (showSpinner) loading.value = true;

    try {
        const { data } = await axios.get("/ajax/admin/calendar/day", {
            params: {
                date: toDateString(selectedDate.value),
                // Sent server-side on purpose: cancelled bookings are ~40-50% of a day's
                // volume, so they are only shipped when actually requested. The remaining
                // filters are cheap and stay local (see buildSchedulerModel).
                booking_filter: filters.booking_filter || 'non_cancelled',
            },
        });

        rawProviders.value      = data.data?.providers       || [];
        rawBookings.value       = data.data?.bookings        || [];
        rawNotes.value          = data.data?.notes           || [];
        rawProviderBlocks.value = data.data?.provider_blocks || [];
        rawAvailability.value   = data.data?.availability    || [];
        rawProviderSpecialDayReasons.value = data.data?.provider_special_day_reasons || {};
        canEditShifts.value = !!data.data?.can_edit_shifts;

        // Dynamic time range adjustment
        const ch = data.data?.company_hours;
        if (ch && ch.is_workday && ch.start && ch.end) {
            isClosed.value = false;
            const startHour = parseInt(ch.start.split(':')[0], 10);
            const endHour   = parseInt(ch.end.split(':')[0],   10);
            calendarConfig.businessBeginsHour = startHour;
            calendarConfig.businessEndsHour   = endHour + 1;
        } else if (ch && !ch.is_workday) {
            isClosed.value = true;
            calendarConfig.businessBeginsHour = 9;
            calendarConfig.businessEndsHour   = 18;
        } else {
            isClosed.value = false;
            calendarConfig.businessBeginsHour = 8;
            calendarConfig.businessEndsHour   = 22;
        }
        companyWindow.value = (ch?.is_workday && ch.start && ch.end)
            ? { startMin: timeToMinutes(ch.start), endMin: timeToMinutes(ch.end) }
            : { startMin: calendarConfig.businessBeginsHour * 60, endMin: calendarConfig.businessEndsHour * 60 };

        buildSchedulerModel(toDateString(selectedDate.value));
        initTimeLine();
    } catch (error) {
        // Only show error toasts for manual loads — suppress noise during polling
        if (showSpinner) {
            toast.add({ severity: 'error', summary: 'Fehler', detail: extractError(error, "Kalenderdaten konnten nicht geladen werden."), life: 5000 });
        }
    } finally {
        if (showSpinner) loading.value = false;
    }
};

/** Manual reload — shows the spinner, triggered by the Laden button or date change */
const loadDay = () => fetchDayData(true);

/** Silent background poll — no spinner, no error toast */
const silentPoll = () => {
    // Skip poll if a modal is open (user is in the middle of editing)
    const anyModalOpen = modalVisible.value || detailModalVisible.value ||
        editModalVisible.value || noteDetailVisible.value ||
        editNoteVisible.value  || editBlockVisible.value  ||
        editBlockSeriesVisible.value;
    if (!anyModalOpen && !shiftDrag.value) {
        fetchDayData(false);
    }
};

/** Page Visibility API — refresh immediately when the tab becomes visible again */
const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
        silentPoll();
    }
};

// Date changes trigger loadDay once
watch(selectedDate, () => {
    loadDay();
});

onMounted(() => {
    calendarConfig.startDate = toDateString(selectedDate.value);
    loadServices();
    loadNoteTypes();

    // Load providers first, then restore any saved quick-filter preset
    loadProviders().then(() => {
        restorePreset();
    });

    loadDay();

    // ── Background polling ─────────────────────────────────────────────────
    // Silently re-fetch every 30 s so both reception computers stay in sync
    pollingInterval = setInterval(silentPoll, POLL_INTERVAL_MS);

    // Refresh immediately whenever this browser tab becomes active again
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // ──────────────────────────────────────────────────────────────────────

    // Refresh time-line position every 60 s (pure math, no DOM reads)
    timeLineInterval = setInterval(updateCurrentTimeLine, 60_000);

    // Dynamically fit calendar to full remaining screen height
    window.addEventListener('resize', updateCalendarHeight);
    window.addEventListener('resize', remeasureOnResize);
    updateCalendarHeight();
    setTimeout(updateCalendarHeight, 200);
});

onUnmounted(() => {
    if (timeLineInterval)  clearInterval(timeLineInterval);
    if (pollingInterval)   clearInterval(pollingInterval);
    window.removeEventListener('resize', updateCalendarHeight);
    window.removeEventListener('resize', remeasureOnResize);
    clearTimeout(_resizeRemeasureTimer);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
});

watch(showFilter, () => {
    setTimeout(updateCalendarHeight, 420); // adapt after filter panel animation
});

const goToday = () => {
    selectedDate.value = new Date();
};

const goPrevDay = () => {
    const next = new Date(selectedDate.value);
    next.setDate(next.getDate() - 1);
    selectedDate.value = next;
};

const goNextDay = () => {
    const next = new Date(selectedDate.value);
    next.setDate(next.getDate() + 1);
    selectedDate.value = next;
};
</script>

<template>
    <Layout>
        <div class="w-full px-2 md:px-6 py-4 pb-20">

        <!-- ── Sticky Header: toolbar + tip + counts ──────────────────── -->
            <div class="sticky top-0 z-30 bg-[#f6f5f1] pt-3 pb-3">

            <!-- Header: 3-column grid — LEFT | CENTER | RIGHT all in one line -->
            <div class="bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm grid grid-cols-[1fr_auto_1fr] items-center gap-2">

                <!-- LEFT: Laden + Show filter + Heute -->
                <div class="flex items-center gap-2">
                    <Button 
                        label="Laden" 
                        icon="pi pi-refresh" 
                        :loading="loading" 
                        @click="loadDay" 
                        outlined 
                        severity="secondary"
                        class="p-button-sm !text-gray-700 !border-gray-300"
                    />
                    <Button 
                        :label="showFilter ? 'Hide filter' : 'Show filter'" 
                        :icon="showFilter ? 'pi pi-filter-slash' : 'pi pi-filter'" 
                        @click="showFilter = !showFilter" 
                        outlined 
                        :severity="showFilter ? 'primary' : 'secondary'"
                        class="p-button-sm !text-gray-700 !border-gray-300"
                    />
                    <Button 
                        label="Heute" 
                        icon="pi pi-calendar" 
                        severity="secondary" 
                        text
                        @click="goToday" 
                        class="!text-indigo-600 font-medium hover:!bg-indigo-50"
                    />
                </div>

                <!-- CENTER: Date navigation (always centred) -->
                <div class="flex items-center gap-1 md:gap-4">
                    <Button 
                        icon="pi pi-chevron-left" 
                        @click="goPrevDay" 
                        text 
                        rounded 
                        class="!text-gray-600 hover:!bg-gray-100"
                    />
                    
                    <div class="relative">
                        <button 
                            @click="toggleCalendar" 
                            class="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors group select-none"
                        >
                            <span class="text-lg md:text-xl font-semibold text-gray-800 group-hover:text-indigo-600 transition-colors">
                                {{ formattedDate }}
                            </span>
                            <div class="w-10 h-10 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center group-hover:bg-indigo-50 group-hover:border-indigo-100 transition-all">
                                <i class="pi pi-calendar text-gray-500 group-hover:text-indigo-600"></i>
                            </div>
                        </button>

                        <!-- Hidden Calendar component for overlay only -->
                        <div class="absolute opacity-0 pointer-events-none w-0 h-0 overflow-hidden">
                            <Calendar 
                                ref="calendarRef" 
                                v-model="selectedDate" 
                                dateFormat="yy-mm-dd" 
                                :firstDayOfWeek="1"
                                :appendTo="'body'"
                                panelClass="admin-calendar-datepicker"
                            />
                        </div>
                    </div>

                    <Button 
                        icon="pi pi-chevron-right" 
                        @click="goNextDay" 
                        text 
                        rounded 
                        class="!text-gray-600 hover:!bg-gray-100"
                    />
                </div>

                <!-- RIGHT: Quick-filter preset buttons (both in one div) -->
                <div class="flex items-center justify-end gap-2">
                    <button
                        @click="applyPreset('infrashape')"
                        :class="[
                            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all select-none border',
                            activePreset === 'infrashape'
                                ? 'bg-violet-600 text-white border-violet-700 shadow-sm'
                                : 'bg-white text-violet-700 border-violet-300 hover:bg-violet-50'
                        ]"
                        title="Zeige nur Infrashape-Provider"
                    >
                        <i :class="activePreset === 'infrashape' ? 'pi pi-check' : 'pi pi-bolt'" style="font-size:11px"></i>
                        Infrashapes
                    </button>

                    <button
                        @click="applyPreset('infrashape_sauna')"
                        :class="[
                            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all select-none border',
                            activePreset === 'infrashape_sauna'
                                ? 'bg-teal-600 text-white border-teal-700 shadow-sm'
                                : 'bg-white text-teal-700 border-teal-300 hover:bg-teal-50'
                        ]"
                        title="Zeige Infrashape + Sauna Provider"
                    >
                        <i :class="activePreset === 'infrashape_sauna' ? 'pi pi-check' : 'pi pi-bolt'" style="font-size:11px"></i>
                        Infrashapes + Saunas
                    </button>
                </div>

            </div>
            <!-- End Header -->

            <div class="text-sm text-gray-500 mt-2 px-2">
                <i class="pi pi-info-circle mr-1"></i> Tip: Shift + Click auf eine Buchung, um den Status schnell zu ändern.
            </div>
    
    <!-- Filter Panel -->
            <Transition name="slide-fade">
                <div v-if="showFilter" class="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        <!-- Client -->
                        <div class="flex flex-col gap-2">
                            <label class="text-sm font-semibold text-gray-600">Client</label>
                            <div class="relative w-full">
                                <AutoComplete 
                                    v-model="filters.customer" 
                                    :suggestions="customerSuggestions" 
                                    @complete="searchCustomers" 
                                    optionLabel="label"
                                    placeholder="Search client..." 
                                    class="w-full"
                                    inputClass="w-full !rounded-lg border-gray-300 !pr-10"
                                />
                                <i class="pi pi-search absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            </div>
                        </div>

                        <!-- Booking code -->
                        <div class="flex flex-col gap-2">
                            <label class="text-sm font-semibold text-gray-600">Booking code</label>
                            <InputText 
                                v-model="filters.booking_code" 
                                placeholder="Enter booking code..." 
                                class="w-full !rounded-lg border-gray-300"
                            />
                        </div>

                        <!-- Show bookings -->
                        <div class="flex flex-col gap-2">
                            <label class="text-sm font-semibold text-gray-600">Show bookings</label>
                            <Dropdown 
                                v-model="filters.booking_filter" 
                                :options="bookingFilterOptions" 
                                optionLabel="label" 
                                optionValue="value"
                                class="w-full !rounded-lg border-gray-300"
                            />
                        </div>

                        <!-- Show notes -->
                        <div class="flex flex-col gap-2">
                            <label class="text-sm font-semibold text-gray-600">Show notes</label>
                            <Dropdown 
                                v-model="filters.show_notes" 
                                :options="showNotesOptions" 
                                optionLabel="label" 
                                optionValue="value"
                                class="w-full !rounded-lg border-gray-300"
                            />
                        </div>

                        <!-- Select service -->
                        <div class="flex flex-col gap-2">
                            <label class="text-sm font-semibold text-gray-600">Select service</label>
                            <div class="relative w-full">
                                <MultiSelect 
                                    v-model="filters.service_ids" 
                                    :options="serviceOptions" 
                                    optionLabel="name" 
                                    placeholder="Select services..." 
                                    :maxSelectedLabels="0"
                                    filter
                                    class="w-full !rounded-lg border-gray-300"
                                    :pt="{
                                        label: { class: '!pr-10' }
                                    }"
                                />
                            </div>
                            <div v-if="filters.service_ids.length > 0" class="flex flex-wrap gap-2 mt-2">
                                <Tag 
                                    v-for="service in filters.service_ids" 
                                    :key="service.uuid" 
                                    :value="service.name" 
                                    severity="info"
                                    class="!bg-gray-50 !text-gray-700 !border-gray-100 !rounded-full px-3"
                                >
                                    <template #icon>
                                        <i class="pi pi-times cursor-pointer text-red-500 hover:text-red-700 mr-2" @click="filters.service_ids = filters.service_ids.filter(s => s.uuid !== service.uuid)"></i>
                                    </template>
                                </Tag>
                            </div>
                        </div>

                        <!-- Select provider -->
                        <div class="flex flex-col gap-2">
                            <label class="text-sm font-semibold text-gray-600">Select provider</label>
                            <div class="relative w-full">
                                <MultiSelect 
                                    v-model="filters.provider_ids" 
                                    :options="providerOptions" 
                                    optionLabel="name" 
                                    placeholder="Select providers..." 
                                    :maxSelectedLabels="0"
                                    filter
                                    class="w-full !rounded-lg border-gray-300"
                                    :pt="{
                                        label: { class: '!pr-10' }
                                    }"
                                />
                            </div>
                            <div v-if="filters.provider_ids.length > 0" class="flex flex-wrap gap-2 mt-2">
                                <Tag 
                                    v-for="provider in filters.provider_ids" 
                                    :key="provider.id" 
                                    :value="provider.name" 
                                    severity="info"
                                    class="!bg-gray-50 !text-gray-700 !border-gray-100 !rounded-full px-3"
                                >
                                    <template #icon>
                                        <i class="pi pi-times cursor-pointer text-red-500 hover:text-red-700 mr-2" @click="filters.provider_ids = filters.provider_ids.filter(p => p.id !== provider.id)"></i>
                                    </template>
                                </Tag>
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-end gap-3 pt-4 border-t border-gray-100">
                        <Button 
                            label="Clear filter" 
                            icon="pi pi-times" 
                            text 
                            @click="clearFilters" 
                            class="!text-gray-500 font-medium hover:!bg-gray-50"
                        />
                        <Button 
                            label="Apply filter" 
                            icon="pi pi-check" 
                            @click="applyFilters" 
                            class="!bg-cyan-500 !border-cyan-500 !rounded-lg px-6 font-semibold shadow-sm hover:!bg-cyan-600"
                        />
                    </div>
                </div>
            </Transition>

            <div class="flex items-center gap-2 pt-2">
                <Tag :value="`Provider: ${providerCount}`" severity="info" />
                <Tag :value="`Buchungen: ${bookingCount}`" severity="success" />
            </div>

            </div>
            <!-- ── End Sticky Header ────────────────────────────────────── -->

            <!-- Calendar grid — DayPilot scrolls internally, column headers stay pinned -->
            <div class="relative bg-white rounded-lg border border-gray-200 p-2 mt-4">
                <div v-if="isClosed" class="absolute inset-0 z-[5] flex items-start justify-center bg-gray-50/40 backdrop-blur-[1px] rounded-lg pt-24">
                    <div class="bg-white/90 border border-gray-200 shadow-xl px-16 py-8 rounded-2xl flex flex-col items-center gap-4 text-center">
                        <div class="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
                            <i class="pi pi-lock text-3xl text-red-500"></i>
                        </div>
                        <div>
                            <h3 class="text-3xl font-extrabold text-gray-800 tracking-tight">GESCHLOSSEN</h3>
                            <p class="text-gray-500 mt-1 font-medium">Das Unternehmen ist an diesem Tag geschlossen.</p>
                        </div>
                    </div>
                </div>

                <!-- Scroll container: takes full remaining screen height, DayPilot content scrolls inside -->
                <div ref="calendarScrollContainerRef" class="calendar-scroll-container">
                <div ref="calendarWrapperRef" :class="{'opacity-40 pointer-events-none grayscale-[20%]': isClosed}" class="relative">
                    <DayPilotCalendar id="admin-calendar" :config="calendarConfig" />

                    <!-- ── Current Time Line ───────────────────────────────── -->
                    <div
                        v-if="showTimeLine && isToday && !isClosed"
                        class="current-time-line"
                        :style="{ top: currentTimeLineTop + 'px' }"
                    >
                        <!-- left dot -->
                        <span class="current-time-dot"></span>
                        <!-- line -->
                        <span class="current-time-track"></span>
                        <!-- label -->
                        <span class="current-time-label">{{ currentTimeLabel }}</span>
                    </div>
                    <!-- ─────────────────────────────────────────────────────── -->

                    <!-- ── Working-hours drag handles ──────────────────────── -->
                    <div
                        v-for="h in shiftHandles"
                        :key="h.key"
                        class="shift-handle"
                        :class="{ 'shift-handle--active': isHandleActive(h) }"
                        :style="{ top: minutesToTop(handleMinutes(h)) + 'px', left: h.left + 'px', width: h.width + 'px' }"
                    >
                        <span class="shift-handle-line"></span>
                        <span class="shift-handle-label">{{ minutesToHHMM(handleMinutes(h)) }}</span>
                        <span
                            class="shift-handle-grip"
                            :title="h.edge === 'start' ? 'Ziehen, um die Schicht zu verschieben' : 'Ziehen, um das Arbeitsende zu ändern'"
                            @pointerdown="startShiftDrag($event, h)"
                            @pointermove="moveShiftDrag"
                            @pointerup="endShiftDrag"
                            @pointercancel="shiftDrag = null"
                        ></span>
                    </div>
                    <!-- ─────────────────────────────────────────────────────── -->
                </div>
                </div><!-- end .calendar-scroll-container -->
            </div>

            <CreateBookingModal 
                v-model:modelValue="modalVisible" 
                :selectedSlot="selectedSlot" 
                :serviceOptions="serviceOptions" 
                :providers="providers"
                :noteTypes="noteTypes"
                @created="() => { modalVisible = false; loadDay(); selectedSlot = null; }" 
            />
            
            <BookingDetailModal 
                v-model:visible="detailModalVisible" 
                :booking="selectedBooking" 
                @edit="openEdit" 
                @cancelled="handleCancelled"
                @refresh="loadDay"
            />

            <EditBookingModal 
                v-model:visible="editModalVisible" 
                :booking="selectedBooking" 
                @updated="handleUpdated"
            />

            <NoteDetailModal 
                v-model:visible="noteDetailVisible"
                :note="selectedNote"
                @edit="(n) => { selectedNote = n; editNoteVisible = true; noteDetailVisible = false; }"
                @deleted="loadDay"
                @refresh="loadDay"
            />

            <EditNoteModal 
                v-model:visible="editNoteVisible"
                :note="selectedNote"
                :noteTypes="noteTypes"
                :providers="providers"
                @updated="loadDay"
            />

            <EditProviderBlockModal 
                v-model:visible="editBlockVisible"
                :block="selectedBlock"
                :providers="providers"
                @updated="loadDay"
                @deleted="loadDay"
                @edit-series="openEditSeriesModal"
            />

            <EditProviderBlockSeriesModal
                v-model:visible="editBlockSeriesVisible"
                :seriesUuid="selectedBlock?.provider_block_series_id ? (selectedBlock.series_uuid || selectedBlock.provider_block_series_id) : null"
                :providers="providers"
                @updated="loadDay"
                @deleted="loadDay"
            />
        </div>
    </Layout>
</template>

<style>
/* ── Sticky Calendar Header ─────────────────────────────────────────────── */
.sticky {
    transition: box-shadow 0.2s ease;
}
/* ─────────────────────────────────────────────────────────────────────────── */

/* ── Current Time Line ─────────────────────────────────────────────────── */
.current-time-line {
    position: absolute;
    left: 0;
    right: 0;
    z-index: 20;
    pointer-events: none;
    display: flex;
    align-items: center;
    /* Center the div vertically on the exact time pixel */
    transform: translateY(-50%);
    /* left offset matches DayPilot time-gutter width (~52 px) */
    padding-left: 52px;
}

.current-time-dot {
    flex-shrink: 0;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #e53935;
    box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.25);
    margin-left: -5px;   /* centre the dot on the line */
    animation: pulse-dot 2s ease-in-out infinite;
}

.current-time-track {
    flex: 1;
    height: 2px;
    background: #e53935;
    box-shadow: 0 0 4px rgba(229, 57, 53, 0.45);
}

.current-time-label {
    flex-shrink: 0;
    margin-left: 6px;
    margin-right: 4px;
    font-size: 11px;
    font-weight: 700;
    color: #e53935;
    background: #fff;
    border: 1px solid rgba(229, 57, 53, 0.4);
    border-radius: 4px;
    padding: 1px 5px;
    line-height: 1.4;
    white-space: nowrap;
    letter-spacing: 0.3px;
}

@keyframes pulse-dot {
    0%, 100% { box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.25); }
    50%       { box-shadow: 0 0 0 6px rgba(229, 57, 53, 0.10); }
}
/* ─────────────────────────────────────────────────────────────────────── */

/* ── Working-hours drag handles ───────────────────────────────────────── */
/* Lets confirm messages break onto separate lines with "\n". */
.p-confirm-dialog-message {
    white-space: pre-line;
}

/* Zero-height anchor on the shift edge; only the grip (in the 22px event-free
   right gutter, see eventMarginRight) takes pointer input, so bookings stay draggable. */
.shift-handle {
    position: absolute;
    height: 0;
    z-index: 21;
    pointer-events: none;
}

.shift-handle-line {
    position: absolute;
    left: 0;
    right: 0;
    top: -1px;
    height: 2px;
    background: #2563eb;
    opacity: 0;
    transition: opacity 0.1s;
}

.shift-handle-label {
    position: absolute;
    right: 24px;
    top: -10px;
    font-size: 11px;
    font-weight: 700;
    line-height: 18px;
    padding: 0 5px;
    color: #fff;
    background: #2563eb;
    border-radius: 4px;
    opacity: 0;
    white-space: nowrap;
}

.shift-handle-grip {
    position: absolute;
    right: 2px;
    top: -5px;
    width: 18px;
    height: 10px;
    border-radius: 5px;
    background: #ffffff;
    border: 1px solid #93c5fd;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
    cursor: ns-resize;
    pointer-events: auto;
    touch-action: none;
}

.shift-handle-grip::before {
    content: "";
    position: absolute;
    left: 4px;
    right: 4px;
    top: 3px;
    height: 2px;
    border-top: 1px solid #60a5fa;
    border-bottom: 1px solid #60a5fa;
}

.shift-handle:hover .shift-handle-line,
.shift-handle--active .shift-handle-line,
.shift-handle--active .shift-handle-label {
    opacity: 1;
}

.shift-handle:hover .shift-handle-grip,
.shift-handle--active .shift-handle-grip {
    background: #2563eb;
    border-color: #2563eb;
}
/* ─────────────────────────────────────────────────────────────────────── */

/* ── Calendar scroll container ────────────────────────────────────────── */
/*
 * Constrain DayPilot to a responsive scroll window so the page itself doesn't scroll.
 * The real DayPilot header row inside stays pinned at top:0 of this container.
 */
.calendar-scroll-container {
    min-height: 440px;
    overflow-y: auto;
    overflow-x: auto;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    -webkit-overflow-scrolling: touch;
}

/* Pin the actual DayPilot header container (corner + provider names) at the top of scroll container */
#admin-calendar > div:first-child {
    position: -webkit-sticky !important;
    position: sticky !important;
    top: 0 !important;
    z-index: 25 !important;
    background-color: #ffffff !important;
    border-top: 1px solid #d1d5db !important;
    border-bottom: 1px solid #d1d5db !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08) !important;
}

/* Ensure solid background on header elements so scrolling appointments never bleed through */
#admin-calendar .calendar_default_corner,
#admin-calendar .calendar_default_corner_inner,
#admin-calendar .calendar_default_cornerright,
#admin-calendar .calendar_default_cornerright_inner,
#admin-calendar .calendar_default_colheader,
#admin-calendar .calendar_default_colheader_inner {
    background-color: #ffffff !important;
}
/* ─────────────────────────────────────────────────────────────────────── */

.calendar_default_cell_inner {
    background-color: #ffffff !important;
}

/* SimplyBook style event styling with comfortable right gutter for quick new booking clicks */
.calendar_default_event {
    border-radius: 4px !important;
    pointer-events: none !important;
}
.calendar_default_event_inner {
    border-radius: 4px !important;
    border: 1px solid rgba(0, 0, 0, 0.22) !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08) !important;
    padding: 0 !important;
    right: 22px !important;
    pointer-events: auto !important;
}

.blocked-cell .calendar_default_cell_inner {
    /* SimplyBook Style: Light yellow-green translucent base with very thin diagonal lines */
    background-color: rgba(221, 221, 128, 0.7) !important;
    background-image: repeating-linear-gradient(
        -45deg,
        rgba(0, 0, 0, 0.1) 0px,
        rgba(0, 0, 0, 0.1) 1px,
        transparent 1px,
        transparent 8px
    ) !important;
}

.special-day-note {
    position: absolute;
    top: 50%;
    left: 3px;
    right: 3px;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    pointer-events: none;
    z-index: 10;
}
.special-day-note span {
    display: inline-block;
    max-width: 100%;
    padding: 1px 5px;
    border-radius: 3px;
    background-color: rgba(255, 255, 255, 0.85);
    font-size: 10px;
    font-weight: 700;
    line-height: 1.4;
    color: #78350f;
    white-space: nowrap;
    overflow: hidden;
    pointer-events: auto;
    text-overflow: ellipsis;
}

.provider-only-cell .calendar_default_cell_inner {
    background-color: #e4e4e4e0 !important;
}

/* .available-cell .calendar_default_cell_inner {
    background-color: #ffffff !important;
    background-image: none !important;
} */

.note-not-blocking {
    opacity: 0.5;
}

.calendar_default_cell {
    border-bottom: none !important;
    position: relative;
}

/* Specific dashed line for hours only using pseudo-element to avoid height/spacing issues */
.hour-line::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    border-bottom: 1px dashed #858585d0;
    pointer-events: none;
}

.calendar_default_cell_inner {
    border-right    : 1px solid #a5a5a5d0 !important;
}

/* Filter Panel Transition */
.slide-fade-enter-active {
  transition: all 0.4s ease-in-out;
  overflow: hidden;
}

.slide-fade-leave-active {
  transition: all 0.3s ease-in-out;
  overflow: hidden;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.slide-fade-enter-to,
.slide-fade-leave-from {
  max-height: 1000px; /* High enough to contain content */
  opacity: 1;
}
</style>
